import React,{useCallback,useEffect,useState} from 'react';
import {motion} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import {
  getAutoConfirmationSettings,
  updateAutoConfirmationDays
} from '../../services/autoConfirmationService';

const {FiClock,FiSave,FiLoader}=FiIcons;

const AdminAutoConfirmationSettingsPanel=()=>{
  const [days,setDays]=useState(4);
  const [savedDays,setSavedDays]=useState(4);
  const [loading,setLoading]=useState(true);
  const [saving,setSaving]=useState(false);
  const [message,setMessage]=useState('');
  const [error,setError]=useState('');

  const loadSettings=useCallback(async()=>{
    setLoading(true);
    setError('');

    try {
      const settings=await getAutoConfirmationSettings();
      const configuredDays=settings?.days_after_scheduled_date || 4;
      setDays(configuredDays);
      setSavedDays(configuredDays);
    } catch (requestError) {
      setError(requestError.message || 'Unable to load auto-confirmation settings.');
    } finally {
      setLoading(false);
    }
  },[]);

  useEffect(()=>{
    loadSettings();
  },[loadSettings]);

  const handleSave=async(event)=>{
    event.preventDefault();
    setMessage('');
    setError('');

    const nextDays=Number(days);

    if (!Number.isInteger(nextDays) || nextDays<1 || nextDays>30) {
      setError('Choose a whole number between 1 and 30 days.');
      return;
    }

    setSaving(true);

    try {
      const result=await updateAutoConfirmationDays(nextDays);
      const configuredDays=result?.days_after_scheduled_date || nextDays;
      setDays(configuredDays);
      setSavedDays(configuredDays);
      setMessage('Auto-confirmation timing updated successfully.');
    } catch (requestError) {
      setError(requestError.message || 'Unable to update auto-confirmation timing.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.section
      initial={{opacity:0,y:10}}
      animate={{opacity:1,y:0}}
      className="mt-8 rounded-[2.5rem] border border-indigo-100 bg-white p-8 shadow-sm"
    >
      <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
        <div className="flex items-start gap-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600">
            <SafeIcon icon={FiClock} />
          </div>
          <div>
            <h2 className="text-xl font-black text-gray-900">
              Automatic Confirmation Window
            </h2>
            <p className="mt-1 max-w-2xl text-xs font-medium leading-5 text-slate-500">
              Buyers and sellers see a live countdown after the scheduled delivery
              date. Orders are automatically confirmed when this window expires.
            </p>
          </div>
        </div>

        <form onSubmit={handleSave} className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <label className="text-[10px] font-black uppercase tracking-widest text-slate-400">
            Days after delivery date
            <input
              type="number"
              min="1"
              max="30"
              step="1"
              value={days}
              disabled={loading || saving}
              onChange={(event)=>setDays(event.target.value)}
              className="mt-2 block w-full rounded-xl bg-slate-50 px-4 py-3 text-sm font-black text-slate-900 outline-none focus:ring-2 focus:ring-indigo-500 sm:w- forty"
            />
          </label>
          <button
            type="submit"
            disabled={loading || saving || Number(days)===savedDays}
            className="flex items-center justify-center rounded-xl bg-indigo-600 px-5 py-3 text-[10px] font-black uppercase tracking-widest text-white disabled:cursor-not-allowed disabled:opacity-50"
          >
            {saving ? (
              <SafeIcon icon={FiLoader} className="mr-2 animate-spin" />
            ) : (
              <SafeIcon icon={FiSave} className="mr-2" />
            )}
            Save Window
          </button>
        </form>
      </div>

      {loading && (
        <p className="mt-5 text-xs font-bold text-indigo-500">
          Loading current setting...
        </p>
      )}

      {message && (
        <p className="mt-5 rounded-xl bg-emerald-50 px-4 py-3 text-xs font-bold text-emerald-700">
          {message}
        </p>
      )}

      {error && (
        <p className="mt-5 rounded-xl bg-red-50 px-4 py-3 text-xs font-bold text-red-700">
          {error}
        </p>
      )}
    </motion.section>
  );
};

export default AdminAutoConfirmationSettingsPanel;